import { useCallback } from 'react';
import { exportCSV, exportJSON, exportPDF } from '../../../../../../config/utils/chartExport';
import { getAllStatisticsAreaIdentifiers, getEfficiencyAreaIdentifiers } from './statisticsDatasetHelpers';
import { buildDatasetDoubleAverages, buildDatasetErrorTotals, formatPercentValue, formatStatValue, toDisplaySpeed } from './statisticsOverlayHelpers';

const STATISTICS_CHART_ID = 'statistics-panel-chart';
const XLSX_ENDPOINT = '/api/v1/productionline/machine_stat_export_xlsx/';
const PDF_ENDPOINT = '/api/v1/productionline/machine_stat_export_pdf/';

export default function useStatisticsExport({
  isSignalOverlapSelected,
  isOverviewSelected,
  activeSignalOverlapArea,
  rawSignalOverlapDataByArea,
  selectedSummary,
  datasets,
  dataByArea,
  selectedDataset,
  datasetData,
  selectedErrorEntries,
  selectedDoubleEntries,
  overviewData,
  overviewEfficiencyDatasets,
  signalOverlapData,
  adjustedSummaryByArea,
  exportFilename,
  exportTitle,
  setExporting
}) {
  const handleExportJSON = useCallback(() => {
    const rowToDbEntry = (area, row) => ({
      identifier: row.identifier ? `FlowData_${row.identifier}` : `FlowData_${area}_STAT`,
      window_start: row.date,
      window_length: row.durationMinutes ?? 1,
      data: {
        areaIdentifier: area,
        aggregateSpeed: row.aggregateSpeed,
        aggregateSpeedAccumulated: row.aggregateSpeedAccumulated,
        runningMetres: row.runningMetres,
        expectedSpeed: row.expectedSpeed,
        contractSpeed: row.contractSpeed,
        itemCount: row.itemCount,
        aggregatedBoolSignals: row.aggregatedBoolSignals,
        noFlowMinutes: row.noFlowMinutes,
        aggregatedMainSignals: row.aggregatedMainSignals,
        materialBefore: row.materialBefore,
        machineAvailable: row.machineAvailable,
        allowanceForward: row.allowanceForward,
        pcDataOk: row.pcDataOk,
        errors: row.errors,
        doubles: row.doubles
      }
    });

    let payload;
    if (isSignalOverlapSelected) {
      payload = {
        area: activeSignalOverlapArea,
        data: rawSignalOverlapDataByArea[activeSignalOverlapArea] ?? []
      };
    } else if (isOverviewSelected) {
      payload = getAllStatisticsAreaIdentifiers(datasets).flatMap((area) => (dataByArea[area] ?? []).map((row) => rowToDbEntry(area, row)));
    } else {
      payload = getEfficiencyAreaIdentifiers(selectedDataset).flatMap((area) => (dataByArea[area] ?? []).map((row) => rowToDbEntry(area, row)));
    }
    exportJSON({ data: payload, filename: exportFilename });
  }, [exportFilename, isSignalOverlapSelected, isOverviewSelected, activeSignalOverlapArea, rawSignalOverlapDataByArea, datasets, dataByArea, selectedDataset]);

  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      let sheets;
      if (isSignalOverlapSelected) {
        sheets = [
          {
            name: 'Signal Overlap',
            columns: [
              { key: 'date', label: 'Window Start' },
              { key: 'materialBefore', label: 'Material Before' },
              { key: 'machineAvailable', label: 'Machine Available' },
              { key: 'allowanceForward', label: 'Allowance Forward' },
              { key: 'pcDataOk', label: 'PC Data OK' },
              { key: 'aggregatedMainSignals', label: 'All Main Signals' }
            ],
            rows: signalOverlapData.map((p) => ({
              date: p.date,
              materialBefore: p.materialBefore,
              machineAvailable: p.machineAvailable,
              allowanceForward: p.allowanceForward,
              pcDataOk: p.pcDataOk,
              aggregatedMainSignals: p.aggregatedMainSignals
            }))
          }
        ];
      } else if (isOverviewSelected) {
        const errorRows = (datasets ?? []).flatMap((dataset) =>
          buildDatasetErrorTotals(dataByArea, dataset).map((entry) => ({
            area: dataset.label,
            signal: entry.name,
            minutes: entry.minutes
          }))
        );
        const sensorRows = (datasets ?? []).flatMap((dataset) =>
          buildDatasetDoubleAverages(dataByArea, dataset).map((entry) => ({
            area: dataset.label,
            sensor: entry.name,
            value: entry.value
          }))
        );
        const summaryRows = [
          {
            area: 'Overview',
            efficiency: selectedSummary?.fullRangeEfficiency ?? null,
            contractEfficiency: selectedSummary?.fullRangeContractEfficiency ?? null,
            actualSpeedAvg: null,
            expectedSpeedAvg: null,
            contractSpeedAvg: null
          },
          ...(datasets ?? []).map((d) => ({
            area: d.label,
            efficiency: adjustedSummaryByArea?.[d.key]?.fullRangeEfficiency ?? null,
            contractEfficiency: adjustedSummaryByArea?.[d.key]?.fullRangeContractEfficiency ?? null,
            actualSpeedAvg: adjustedSummaryByArea?.[d.key]?.actualSpeedAverage ?? null,
            expectedSpeedAvg: adjustedSummaryByArea?.[d.key]?.expectedSpeedAverage ?? null,
            contractSpeedAvg: adjustedSummaryByArea?.[d.key]?.contractSpeedAverage ?? null
          }))
        ];
        sheets = [
          {
            name: 'Time Series',
            columns: [
              { key: 'date', label: 'Window Start' },
              ...overviewEfficiencyDatasets.map((d) => ({ key: `efficiency_${d.key}`, label: `${d.label} Efficiency (%)` })),
              { key: 'averageEfficiency', label: 'Average Efficiency (%)' }
            ],
            rows: overviewData
          },
          {
            name: 'Summary',
            columns: [
              { key: 'area', label: 'Area' },
              { key: 'efficiency', label: 'Efficiency (%)' },
              { key: 'contractEfficiency', label: 'Contract Efficiency (%)' },
              { key: 'actualSpeedAvg', label: 'Actual Speed Avg' },
              { key: 'expectedSpeedAvg', label: 'Expected Speed Avg' },
              { key: 'contractSpeedAvg', label: 'Contract Speed Avg' }
            ],
            rows: summaryRows
          },
          ...(errorRows.length
            ? [
                {
                  name: 'Error Minutes',
                  columns: [
                    { key: 'area', label: 'Area' },
                    { key: 'signal', label: 'Signal' },
                    { key: 'minutes', label: 'Total Minutes' }
                  ],
                  rows: errorRows
                }
              ]
            : []),
          ...(sensorRows.length
            ? [
                {
                  name: 'Sensor Values',
                  columns: [
                    { key: 'area', label: 'Area' },
                    { key: 'sensor', label: 'Sensor' },
                    { key: 'value', label: 'Latest Value' }
                  ],
                  rows: sensorRows
                }
              ]
            : [])
        ];
      } else {
        const areaIdentifiers = getEfficiencyAreaIdentifiers(selectedDataset);
        const speedUnit = selectedDataset?.speedUnit;
        const speedUnitSuffix = speedUnit ? ` (${speedUnit})` : '';
        const tsRows = areaIdentifiers.length === 1 ? dataByArea[areaIdentifiers[0]] ?? [] : datasetData;
        const displayRows =
          speedUnit === 'm/h'
            ? tsRows.map((point) => {
                // Single-area export rows contain interval totals; datasetData already contains rates.
                const duration = areaIdentifiers.length === 1 ? Math.max(point.durationMinutes ?? 1, 1) : 1;
                const displaySpeed = (value) => toDisplaySpeed(typeof value === 'number' ? value / duration : value, speedUnit);
                return {
                  ...point,
                  actualSpeed: displaySpeed(point.actualSpeed),
                  expectedSpeed: displaySpeed(point.expectedSpeed),
                  contractSpeed: displaySpeed(point.contractSpeed)
                };
              })
            : tsRows;
        const tsColumns = [
          { key: 'date', label: 'Window Start' },
          { key: 'durationMinutes', label: 'Duration (min)' },
          { key: 'actualSpeed', label: `${selectedDataset?.actualSpeedLabel ?? 'Actual Speed'}${speedUnitSuffix}` },
          { key: 'expectedSpeed', label: `${selectedDataset?.expectedSpeedLabel ?? 'Expected Speed'}${speedUnitSuffix}` },
          { key: 'contractSpeed', label: `Contract Speed${speedUnitSuffix}` },
          { key: 'efficiency', label: 'Efficiency (%)' },
          { key: 'contractEfficiency', label: 'Contract Efficiency (%)' },
          ...(areaIdentifiers.length === 1
            ? [
                { key: 'noFlowMinutes', label: 'No-Flow Minutes' },
                { key: 'itemCount', label: 'Item Count' }
              ]
            : [])
        ];
        sheets = [
          { name: 'Time Series', columns: tsColumns, rows: displayRows },
          {
            name: 'Summary',
            columns: [
              { key: 'efficiency', label: 'Efficiency (%)' },
              { key: 'contractEfficiency', label: 'Contract Efficiency (%)' },
              { key: 'actualSpeedAvg', label: `${selectedDataset?.actualSpeedLabel ?? 'Actual Speed Avg'}${speedUnitSuffix}` },
              { key: 'expectedSpeedAvg', label: `${selectedDataset?.expectedSpeedLabel ?? 'Expected Speed Avg'}${speedUnitSuffix}` },
              { key: 'contractSpeedAvg', label: `Contract Speed Avg${speedUnitSuffix}` }
            ],
            rows: [
              {
                efficiency: selectedSummary?.fullRangeEfficiency ?? null,
                contractEfficiency: selectedSummary?.fullRangeContractEfficiency ?? null,
                actualSpeedAvg: toDisplaySpeed(selectedSummary?.actualSpeedAverage, speedUnit) ?? null,
                expectedSpeedAvg: toDisplaySpeed(selectedSummary?.expectedSpeedAverage, speedUnit) ?? null,
                contractSpeedAvg: toDisplaySpeed(selectedSummary?.contractSpeedAverage, speedUnit) ?? null
              }
            ]
          },
          ...(selectedErrorEntries.length
            ? [
                {
                  name: 'Error Minutes',
                  columns: [
                    { key: 'signal', label: 'Signal' },
                    { key: 'minutes', label: 'Total Minutes' }
                  ],
                  rows: selectedErrorEntries.map((e) => ({ signal: e.name, minutes: e.minutes }))
                }
              ]
            : []),
          ...(selectedDoubleEntries.length
            ? [
                {
                  name: 'Sensor Values',
                  columns: [
                    { key: 'sensor', label: 'Sensor' },
                    { key: 'value', label: 'Latest Value' }
                  ],
                  rows: selectedDoubleEntries.map((e) => ({ sensor: e.name, value: e.value }))
                }
              ]
            : [])
        ];
      }
      await exportCSV({ endpoint: XLSX_ENDPOINT, sheets, filename: exportFilename });
    } finally {
      setExporting(false);
    }
  }, [
    exportFilename,
    isSignalOverlapSelected,
    isOverviewSelected,
    signalOverlapData,
    overviewData,
    overviewEfficiencyDatasets,
    datasets,
    dataByArea,
    adjustedSummaryByArea,
    selectedSummary,
    selectedDataset,
    datasetData,
    selectedErrorEntries,
    selectedDoubleEntries,
    setExporting
  ]);

  const handleExportPDF = useCallback(async () => {
    setExporting(true);
    try {
      let summaryItems = [];
      const tables = [];
      if (!isSignalOverlapSelected) {
        if (isOverviewSelected) {
          summaryItems = [
            { label: 'Average Efficiency', value: formatPercentValue(selectedSummary?.fullRangeEfficiency) },
            { label: 'Contract Efficiency', value: formatPercentValue(selectedSummary?.fullRangeContractEfficiency) }
          ];
          const areaRows = (datasets ?? []).map((d) => ({
            area: d.label,
            efficiency: formatPercentValue(adjustedSummaryByArea?.[d.key]?.fullRangeEfficiency),
            contractEfficiency: formatPercentValue(adjustedSummaryByArea?.[d.key]?.fullRangeContractEfficiency)
          }));
          if (areaRows.length) {
            tables.push({
              title: 'Area Summary',
              columns: [
                { key: 'area', label: 'Area' },
                { key: 'efficiency', label: 'Efficiency' },
                { key: 'contractEfficiency', label: 'Contract Efficiency' }
              ],
              rows: areaRows
            });
          }
          const errorRows = (datasets ?? []).flatMap((dataset) =>
            buildDatasetErrorTotals(dataByArea, dataset).map((entry) => ({
              area: dataset.label,
              signal: entry.name,
              minutes: formatStatValue(entry.minutes)
            }))
          );
          if (errorRows.length) {
            tables.push({
              title: 'Error Minutes',
              columns: [
                { key: 'area', label: 'Area' },
                { key: 'signal', label: 'Signal' },
                { key: 'minutes', label: 'Minutes' }
              ],
              rows: errorRows
            });
          }
          const sensorRows = (datasets ?? []).flatMap((dataset) =>
            buildDatasetDoubleAverages(dataByArea, dataset).map((entry) => ({
              area: dataset.label,
              sensor: entry.name,
              value: formatStatValue(entry.value)
            }))
          );
          if (sensorRows.length) {
            tables.push({
              title: 'Sensor Values',
              columns: [
                { key: 'area', label: 'Area' },
                { key: 'sensor', label: 'Sensor' },
                { key: 'value', label: 'Latest Value' }
              ],
              rows: sensorRows
            });
          }
        } else {
          summaryItems = [
            { label: 'Efficiency', value: formatPercentValue(selectedSummary?.fullRangeEfficiency) },
            { label: 'Contract Efficiency', value: formatPercentValue(selectedSummary?.fullRangeContractEfficiency) },
            {
              label: selectedDataset?.actualSpeedLabel ?? 'Actual Speed Avg',
              value: formatStatValue(toDisplaySpeed(selectedSummary?.actualSpeedAverage, selectedDataset?.speedUnit), 2, selectedDataset?.speedUnit)
            },
            {
              label: selectedDataset?.expectedSpeedLabel ?? 'Expected Speed Avg',
              value: formatStatValue(toDisplaySpeed(selectedSummary?.expectedSpeedAverage, selectedDataset?.speedUnit), 2, selectedDataset?.speedUnit)
            },
            { label: 'Contract Speed Avg', value: formatStatValue(toDisplaySpeed(selectedSummary?.contractSpeedAverage, selectedDataset?.speedUnit), 2, selectedDataset?.speedUnit) }
          ];
          if (selectedErrorEntries.length) {
            tables.push({
              title: 'Error Minutes',
              columns: [
                { key: 'signal', label: 'Signal' },
                { key: 'minutes', label: 'Minutes' }
              ],
              rows: selectedErrorEntries.map((e) => ({ signal: e.name, minutes: formatStatValue(e.minutes) }))
            });
          }
          if (selectedDoubleEntries.length) {
            tables.push({
              title: 'Sensor Values',
              columns: [
                { key: 'sensor', label: 'Sensor' },
                { key: 'value', label: 'Latest Value' }
              ],
              rows: selectedDoubleEntries.map((e) => ({ sensor: e.name, value: formatStatValue(e.value) }))
            });
          }
        }
      }
      await exportPDF({
        endpoint: PDF_ENDPOINT,
        chartContainerId: STATISTICS_CHART_ID,
        filename: exportFilename,
        title: exportTitle,
        summaryItems,
        tables
      });
    } finally {
      setExporting(false);
    }
  }, [
    exportFilename,
    exportTitle,
    selectedDataset,
    isSignalOverlapSelected,
    isOverviewSelected,
    selectedSummary,
    datasets,
    dataByArea,
    adjustedSummaryByArea,
    selectedErrorEntries,
    selectedDoubleEntries,
    setExporting
  ]);

  return { handleExportJSON, handleExportCSV, handleExportPDF };
}
